import React, { useMemo } from 'react';
import { InfoModal, InfoModalProps } from '../../../../theme/components';
import { useDevicesControlUnits } from '../../../../redux/controlUnit/hooks/useDevicesControlUnits';
import {
  Accordion,
  AccordionSummary,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { getTranslationNameByData } from '../../../../redux/monitoring/api/util/getLabelName';
import { useCUsTranslate } from '../../../../redux/CUTranslation/hooks/useCUsTranslate';
import { getPolyglot } from '../../../../i18n';
import { isNotNullOrUndefined } from '../../../../util/types/Array';
import {
  CHANNEL_TYPE,
  GlobalChannelID,
} from '../../../../redux/reports/api/Report.model';
import useChannelsFromComposableDevice from '../../../../redux/composableDevice/hooks/useChannelsFromComposableDevice';
import SelectChannelModalComposableDevice from './SelectChannelModalComposableDevice';
const KEY_CONTROL_UNITS_BY_TYPE = 'channels';
export interface SelectChannelModalProps extends InfoModalProps {
  onSelectItem: (item: GlobalChannelID) => void;
  groupId: string | undefined;
}
function SelectChannelModal(props: SelectChannelModalProps) {
  const { onSelectItem, groupId } = props;
  const [devicesControlUnits, loadingDevicesControlUnits] =
    useDevicesControlUnits();
  const [CUsTranslate, loadingTranslations] = useCUsTranslate();
  const polyglot = getPolyglot();

  const notEmptyDevicesControlUnits = useMemo(
    () => devicesControlUnits.filter((device) => device.controlUnits?.length),
    [devicesControlUnits]
  );

  const { data: composableChannels } = useChannelsFromComposableDevice(
    groupId ?? ''
  );
  if (composableChannels.length) {
    return <SelectChannelModalComposableDevice {...props} />;
  }

  return (
    <InfoModal {...props} maxWidth="sm" fullWidth>
      <Box className="DigitalChannelFilters" sx={{ width: '100%' }}>
        {loadingDevicesControlUnits && (
          <Skeleton variant="rectangular" width="560px" height={118} />
        )}
        {notEmptyDevicesControlUnits.map((zipDeviceControlUnit) => (
          <Accordion
            key={zipDeviceControlUnit.deviceId}
            id={`device-title-${zipDeviceControlUnit.deviceId}`}
            aria-controls={`device-title-${zipDeviceControlUnit.deviceId}`}
            className={`DigitalChannelFilters__Device Device-${zipDeviceControlUnit.deviceId}`}
            defaultExpanded={true}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h5" display="flex" alignItems="center">
                {zipDeviceControlUnit.deviceName}
              </Typography>
            </AccordionSummary>
            <Box sx={{ padding: 1 }}>
              {zipDeviceControlUnit.controlUnits
                ?.filter(
                  (controlUnit) =>
                    controlUnit[KEY_CONTROL_UNITS_BY_TYPE]?.length
                )
                .map((controlUnit) => (
                  <Accordion
                    key={controlUnit.id}
                    className={`DigitalChannelFilters__ControlUnit Device-${zipDeviceControlUnit.deviceId} ControlUnit-${controlUnit.id}`}
                    id={`control-unit-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}`}
                    aria-controls={`control-unit-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}`}
                    defaultExpanded={true}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography
                        variant="h6"
                        display="flex"
                        alignItems="center"
                      >
                        {controlUnit.name ?? controlUnit.id}
                      </Typography>
                    </AccordionSummary>
                    <List>
                      {controlUnit[KEY_CONTROL_UNITS_BY_TYPE]?.map(
                        (channel) => {
                          const primaryText = getTranslationNameByData(
                            channel?.name,
                            channel.id,
                            CUsTranslate,
                            zipDeviceControlUnit.deviceId,
                            controlUnit.id
                          );

                          const longTranslation =
                            (CUsTranslate(
                              channel?.name ?? channel.id,
                              zipDeviceControlUnit.deviceId,
                              controlUnit.id,
                              'ram',
                              'longText',
                              false,
                              true
                            ) as string) ||
                            (CUsTranslate(
                              channel?.name ?? channel.id,
                              zipDeviceControlUnit.deviceId,
                              controlUnit.id,
                              'ram',
                              'longText',
                              false,
                              true
                            ) as string);

                          const secondaryText = [
                            channel.id
                              ? `${polyglot.t('device.monitoring.id_taq')}: ${
                                  channel.id
                                }`
                              : undefined,
                            channel.name
                              ? `${polyglot.t('device.monitoring.name_taq')}: ${
                                  channel.name
                                }`
                              : undefined,
                            longTranslation
                              ? `${polyglot.t(
                                  'device.monitoring.description_taq'
                                )}: ${longTranslation}`
                              : undefined,
                          ]
                            .filter(isNotNullOrUndefined)
                            .join(', ');

                          return (
                            <ListItemButton
                              key={channel.id}
                              className={`DigitalChannelFilters__Event Event-${channel.id}`}
                              role={undefined}
                              onClick={() =>
                                onSelectItem({
                                  deviceID: zipDeviceControlUnit.deviceId,
                                  cuID: controlUnit.id,
                                  channelID: channel.id,
                                  type: CHANNEL_TYPE.ANALOG,
                                })
                              }
                              dense
                            >
                              {loadingTranslations ? (
                                <Skeleton width="100%" height={24} />
                              ) : (
                                <ListItemText
                                  id={`event-title-${zipDeviceControlUnit.deviceId}-${controlUnit.id}-${channel.id}-label`}
                                  primary={primaryText}
                                  secondary={secondaryText}
                                  primaryTypographyProps={{
                                    sx: { wordBreak: 'break-all' },
                                  }}
                                  secondaryTypographyProps={{
                                    sx: { wordBreak: 'break-all' },
                                  }}
                                />
                              )}
                            </ListItemButton>
                          );
                        }
                      )}
                    </List>
                  </Accordion>
                ))}
            </Box>
          </Accordion>
        ))}
      </Box>
    </InfoModal>
  );
}

export default SelectChannelModal;
